import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient } from '@/services/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  Mic, 
  Filter, 
  X,
  MessageSquare,
  BookOpen,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { KnowledgeCategory } from './types';

interface SearchResult {
  id: string;
  title: string;
  excerpt: string;
  category: KnowledgeCategory;
  tags: string[];
  relevance: number;
}

interface KnowledgeSearchProps {
  onResultSelect?: (resultId: string) => void;
  onAskAgent?: (query: string) => void;
}

export function KnowledgeSearch({ onResultSelect, onAskAgent }: KnowledgeSearchProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<Set<KnowledgeCategory>>(new Set());

  const { data: dbResults = [], isLoading: isSearching } = useQuery({
    queryKey: ['knowledge-search', query],
    queryFn: async () => {
      if (!query || query.length < 2) return [];
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('faq_questions')
        .select('id, question_text, answer_text, topic_category, language')
        .eq('is_active', true)
        .or(`question_text.ilike.%${query}%,answer_text.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []).map((faq: any) => ({
        id: faq.id,
        title: faq.question_text || 'Untitled',
        excerpt: (faq.answer_text || '').substring(0, 150) + ((faq.answer_text?.length ?? 0) > 150 ? '...' : ''),
        category: (faq.topic_category?.toLowerCase() || 'crops') as KnowledgeCategory,
        tags: [faq.topic_category?.toLowerCase() || 'general'],
        relevance: 1,
      }));
    },
    enabled: query.length >= 2,
    staleTime: 1000 * 30,
  });

  const searchResults = useMemo(() => {
    let filtered = dbResults;
    if (selectedCategories.size > 0) {
      filtered = filtered.filter(r => selectedCategories.has(r.category));
    }
    return filtered;
  }, [dbResults, selectedCategories]);

  const handleVoiceSearch = () => {
    setIsRecording(!isRecording);
  };

  const toggleCategory = (category: KnowledgeCategory) => {
    setSelectedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const categories: { id: KnowledgeCategory; label: string }[] = [
    { id: 'crops', label: t('knowledge.tabs.crops') },
    { id: 'pests', label: t('features.knowledge.pestsAndDiseases') },
    { id: 'weather', label: t('knowledge.tabs.weather') },
    { id: 'markets', label: t('features.knowledge.markets') },
    { id: 'best_practices', label: t('features.knowledge.bestPractices') },
  ];

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Search Bar */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
          <Input
            placeholder={t('features.knowledge.searchKnowledgeBase')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 pr-24"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleVoiceSearch}
            >
              <Mic className={isRecording ? 'text-red-500 animate-pulse' : ''} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t('features.knowledge.filterByCategory')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {categories.map(cat => (
                  <DropdownMenuCheckboxItem
                    key={cat.id}
                    checked={selectedCategories.has(cat.id)}
                    onCheckedChange={() => toggleCategory(cat.id)}
                  >
                    {cat.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Active Filters */}
        {selectedCategories.size > 0 && (
          <div className="flex flex-wrap gap-2">
            {Array.from(selectedCategories).map(cat => (
              <Badge key={cat} variant="secondary" className="gap-1">
                {categories.find(c => c.id === cat)?.label}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => toggleCategory(cat)}
                />
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {query.length < 2 ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('features.knowledge.typeToSearch')}
              </p>
            </div>
          ) : isSearching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground">
                  {t('features.knowledge.resultsFound', { count: searchResults.length })}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAskAgent?.(query)}
                  className="gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  {t('features.knowledge.askAgriAgent')}
                </Button>
              </div>

              {searchResults.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">{t('features.knowledge.noResultsFound')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('features.knowledge.tryDifferentKeywords')}
                  </p>
                  <Button onClick={() => onAskAgent?.(query)}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {t('features.knowledge.askAgriAgentInstead')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map(result => (
                    <Card
                      key={result.id}
                      className="p-4 cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => onResultSelect?.(result.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 mb-2">
                            <h3 className="font-semibold text-foreground flex-1">
                              {result.title}
                            </h3>
                            <Badge variant="outline" className="flex-shrink-0 text-xs">
                              {categories.find(c => c.id === result.category)?.label || result.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {result.excerpt}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {result.tags.slice(0, 3).map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
